import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TablePagination,
  Grid,
  Alert,
  Snackbar,
  Divider,
  Card,
  CardHeader,
  CardContent,
  CardActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Container
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  Folder as FolderIcon,
  Assignment as AssignmentIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  FilterList as FilterListIcon
} from '@mui/icons-material';
import registryService from '../services/registryService';
import useCan from '../hooks/useCan';
import RegistryForm from '../components/RegistryForm';
import RegistryTable from '../components/RegistryTable';
import RegistryEntriesDialog from '../components/RegistryEntriesDialog';
import RegistrySyncPanel from '../components/RegistrySyncPanel';
import ConfirmDialog from '../components/ConfirmDialog';
import SchemaEditor from '../components/SchemaEditor';
import SourceConfigEditor from '../components/SourceConfigEditor';
import { coerceEntryAttributesToSchema } from '../utils/registryAttributes';
import { isSchemaFlagTrue, orderedPropertyEntries } from '../utils/registrySchemaUtils';
import { fieldErrorsByPath, pruneMapping } from '../utils/registrySourceConfig';
import useRegistryEntries from '../hooks/useRegistryEntries';

// Dati mock temporanei per quando l'API non è disponibile
const mockRegistries = [
  {
    id: 1,
    name: 'Tipi di Documento',
    description: 'Tipologie di documenti utilizzabili',
    entryCount: 5,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 2,
    name: 'Classificazione Privacy',
    description: 'Livelli di classificazione per la privacy',
    entryCount: 3,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  },
  {
    id: 3,
    name: 'Dipartimenti',
    description: 'Dipartimenti aziendali',
    entryCount: 4,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  }
];

// Campi chiave dello schema (flag `key` letto come dal backend: true o "true").
const keyFieldsOf = (registry) => Object.entries(registry?.schema?.properties || {})
  .filter(([, p]) => isSchemaFlagTrue(p?.key))
  .map(([k]) => k);

// Un valore di chiave c'è se non è null/undefined e, per le stringhe, non è vuoto dopo il trim
// (il backend considera assente una chiave vuota, che quindi può ancora essere assegnata).
const hasKeyValue = (value) => value !== null && value !== undefined
  && (typeof value !== 'string' || value.trim() !== '');

// Campi non modificabili nella modifica di una voce: le chiavi che la voce ha già valorizzate.
const lockedFieldsOf = (registry, entry) => keyFieldsOf(registry)
  .filter((field) => hasKeyValue(entry?.attributes?.[field]));

// Ultima pagina esistente se `response` è una pagina vuota oltre la fine (pagina > 0 ma totale > 0),
// altrimenti null: con un totale 0, o se è già l'ultima, la pagina vuota va mostrata com'è (niente ciclo).
const lastPageIfPastEnd = (response, page, size) => {
  const total = Number(response?.totalElements) || 0;
  if (!Array.isArray(response?.content) || response.content.length > 0 || page === 0 || total === 0) return null;
  const last = Math.max(0, Math.ceil(total / size) - 1);
  return last === page ? null : last;
};

// Dettaglio dei fieldErrors del backend ("campo — messaggio; ..."), con l'etichetta del campo.
const fieldErrorDetails = (data, labelOf = (field) => field) => (Array.isArray(data?.fieldErrors)
  ? data.fieldErrors.map((fe) => `${labelOf(fe.field)} — ${fe.message}`).join('; ')
  : '');

// Errori su un'anagrafica che l'utente può correggere (validazione 400; 409 per eliminazione,
// cambio chiave o flag interna mentre è legata a tipi codex): con fieldErrors solo le righe per
// campo, altrimenti il messaggio del backend. Per tutto il resto (404, 5xx, rete) il fallback.
const registryErrorMessage = (err, fallback) => {
  const status = err?.response?.status;
  const data = err?.response?.data;
  if ((status !== 400 && status !== 409) || !data?.message) return fallback;
  return fieldErrorDetails(data) || data.message;
};

// Messaggio d'errore del salvataggio di una voce: con fieldErrors solo le righe per campo
// (`attributes.<chiave>` mostrato con il nome dichiarato nello schema), altrimenti il messaggio.
const saveErrorMessage = (err, schema) => {
  if (err?.response?.status === 409) return 'Voce duplicata (chiave già presente)';
  const data = err?.response?.data;
  const labelOf = (field) => {
    const key = String(field || '').replace(/^attributes\./, '');
    return schema?.properties?.[key]?.name || key;
  };
  return fieldErrorDetails(data, labelOf) || data?.message || 'Errore nel salvataggio della voce';
};

// Valori stringa senza spazi ai bordi (una chiave "  ABC  " non deve diventare un quasi-duplicato di "ABC").
const trimStringAttributes = (attributes) => Object.fromEntries(
  Object.entries(attributes || {}).map(([key, value]) => [key, typeof value === 'string' ? value.trim() : value])
);

const entryLabel = (entry) => entry?.entryKey || entry?.id;

// Campi del DomainRegistryDto che il dialogo può inviare: il backend rifiuta i campi sconosciuti.
const REGISTRY_EDITABLE_FIELDS = ['code', 'name', 'description', 'schema', 'sourceType', 'sourceConfig', 'internal'];

// Payload di create/update: solo i campi del DTO (più id e version in modifica), senza mutare lo stato.
const registryPayload = (registry, isNew) => {
  const fields = isNew ? REGISTRY_EDITABLE_FIELDS : ['id', 'version', ...REGISTRY_EDITABLE_FIELDS];
  const payload = {};
  fields.forEach((field) => {
    if (registry[field] !== undefined) payload[field] = registry[field];
  });
  if (isNew && payload.internal && !payload.sourceConfig) payload.sourceConfig = {};
  // Rete di sicurezza: un mapping verso un campo che lo schema non ha più farebbe fallire il salvataggio.
  if (payload.sourceConfig) payload.sourceConfig = pruneMapping(payload.sourceConfig, registry.schema);
  return payload;
};

const Registries = () => {
  const can = useCan();
  // L'elenco si apre con registries.read; creare, salvare le modifiche ed eliminare chiedono
  // il proprio permesso, altrimenti il comando porta a un 403. Il dialogo di modifica si apre
  // comunque: chi legge soltanto vede la configurazione, ma senza «Salva».
  const puoCreare = can('registries.create');
  const puoModificare = can('registries.update');
  const puoEliminare = can('registries.delete');

  // Stati per la lista delle anagrafiche
  const [registries, setRegistries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [usingMockData, setUsingMockData] = useState(false);
  
  // Stati per il dialogo di anagrafica
  const [openRegistryDialog, setOpenRegistryDialog] = useState(false);
  const [currentRegistry, setCurrentRegistry] = useState(null);
  // Errori di validazione della sorgente dati (fieldErrors del 400) per percorso, mostrati dall'editor.
  const [sourceErrors, setSourceErrors] = useState({});
  const [isNewRegistry, setIsNewRegistry] = useState(true);
  
  // Stati per il dialogo di voci nell'anagrafica
  const [openEntriesDialog, setOpenEntriesDialog] = useState(false);
  const [selectedRegistry, setSelectedRegistry] = useState(null);
  const [syncRegistry, setSyncRegistry] = useState(null);
  const entriesState = useRegistryEntries(openEntriesDialog ? selectedRegistry : null);
  const [entryToDelete, setEntryToDelete] = useState(null);
  const [registryToDelete, setRegistryToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  // Salvataggi in corso: il ref blocca i click ravvicinati prima del re-render, lo stato disabilita il bottone
  const [savingRegistry, setSavingRegistry] = useState(false);
  const savingRegistryRef = useRef(false);
  const [savingEntry, setSavingEntry] = useState(false);
  const savingEntryRef = useRef(false);
  // Numero dell'ultima richiesta della lista: una risposta in ritardo non sovrascrive una più recente
  const listRequestId = useRef(0);
  // Ultima etichetta mostrata nei dialoghi di conferma: resta visibile durante la transizione di chiusura
  const lastEntryLabel = useRef('');
  const lastRegistryLabel = useRef('');
  if (entryToDelete) lastEntryLabel.current = entryLabel(entryToDelete);
  if (registryToDelete) lastRegistryLabel.current = registryToDelete.name || registryToDelete.code;
  
  // Stati per la creazione/modifica di una voce
  const [openEntryDialog, setOpenEntryDialog] = useState(false);
  const [currentEntry, setCurrentEntry] = useState(null);
  const [entryFormData, setEntryFormData] = useState({});
  const [isNewEntry, setIsNewEntry] = useState(true);
  // In modifica le chiavi già valorizzate non si cambiano; una chiave ancora vuota si può assegnare.
  const lockedEntryFields = isNewEntry ? [] : lockedFieldsOf(selectedRegistry, currentEntry);
  // Le voci si scrivono solo sulle anagrafiche interne (sulle esterne il backend risponde 400).
  const puoModificareVoci = puoModificare && selectedRegistry?.internal === true;
  
  // Stato per lo snackbar
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Stato per la validità live dello SchemaEditor (righe correnti, non lo schema ri-serializzato)
  const [schemaErrors, setSchemaErrors] = useState([]);
  
  // Stato per il menu contestuale
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRegistryForMenu, setSelectedRegistryForMenu] = useState(null);
  
  // Stati per la gestione della paginazione
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Caricamento iniziale delle anagrafiche
  useEffect(() => {
    fetchRegistries();
  
  }, []);
  
  const handlePageChange = (event, newPage) => {
    setPage(newPage);
    fetchRegistries(newPage, rowsPerPage, searchTerm);
  };

  const handleRowsPerPageChange = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    fetchRegistries(0, newRowsPerPage, searchTerm);
  };

  const handleSearch = (searchTerm) => {
    setSearchTerm(searchTerm);
    setPage(0);
    fetchRegistries(0, rowsPerPage, searchTerm);
  };
  
  // Carica tutte le anagrafiche
  const fetchRegistries = async (page = 0, size = 10, search = '') => {
    const id = ++listRequestId.current;
    try {
      setLoading(true);
      let currentPage = page;
      let response = await registryService.getAll(currentPage, size, search);
      if (id !== listRequestId.current) return; // superata da una richiesta più recente

      // pagina oltre l'ultima (es. dopo un salvataggio o un'eliminazione altrove): vai all'ultima esistente
      for (let last = lastPageIfPastEnd(response, currentPage, size); last !== null;
        last = lastPageIfPastEnd(response, currentPage, size)) {
        currentPage = last;
        response = await registryService.getAll(currentPage, size, search);
        if (id !== listRequestId.current) return;
      }
      if (currentPage !== page) setPage(currentPage);

      // Verifica che i dati ricevuti siano un array
      if (Array.isArray(response.content)) {
        setRegistries(response.content);
        setTotalCount(response.totalElements);
        setUsingMockData(false);
        setError(null); // recovered: clear any stale demo-mode banner
      } else {
        console.warn('API response is not an array, using mock data');
        setRegistries(mockRegistries);
        setTotalCount(mockRegistries.length);
        setUsingMockData(true);
        setError('Il server API non è completamente implementato, visualizzazione dati dimostrativi');
      }
    } catch (err) {
      if (id !== listRequestId.current) return;
      console.error('Error fetching registries:', err);
      setRegistries(mockRegistries); // Usa dati mock in caso di errore
      setTotalCount(mockRegistries.length);
      setUsingMockData(true);
      setError('Impossibile connettersi al server API, visualizzazione dati dimostrativi');
    } finally {
      if (id === listRequestId.current) setLoading(false);
    }
  };
  
  // Apertura del dialogo per creare una nuova anagrafica
  const handleOpenNewRegistryDialog = () => {
    setCurrentRegistry({
      name: '',
      description: '',
      internal: true,
      schema: { properties: {} }
    });
    setSourceErrors({});
    setIsNewRegistry(true);
    setOpenRegistryDialog(true);
  };
  
  // Apertura del dialogo per modificare un'anagrafica esistente
  const handleOpenEditRegistryDialog = (registry) => {
    setCurrentRegistry({ ...registry });
    setSourceErrors({});
    setIsNewRegistry(false);
    setOpenRegistryDialog(true);
    setAnchorEl(null);
  };
  
  // Chiusura del dialogo dell'anagrafica
  const handleCloseRegistryDialog = () => {
    setOpenRegistryDialog(false);
    setCurrentRegistry(null);
    setSourceErrors({});
  };
  
  // Salvataggio dell'anagrafica (creazione o modifica)
  const handleSaveRegistry = async () => {
    if (savingRegistryRef.current) return; // click ravvicinati: una sola richiesta
    setSourceErrors({});
    try {
      if (!currentRegistry.name) {
        setSnackbar({
          open: true,
          message: 'Il nome dell\'anagrafica è obbligatorio',
          severity: 'error'
        });
        return;
      }

      if (!currentRegistry.code || !currentRegistry.code.trim()) {
        setSnackbar({
          open: true,
          message: 'Il codice dell\'anagrafica è obbligatorio',
          severity: 'error'
        });
        return;
      }

      if (schemaErrors.length > 0) {
        setSnackbar({ open: true, message: `Schema non valido: ${schemaErrors[0].message}`, severity: 'error' });
        return;
      }
      savingRegistryRef.current = true;
      setSavingRegistry(true);
      const payload = registryPayload(currentRegistry, isNewRegistry);

      let savedRegistry;
      
      if (usingMockData) {
        // Simula il salvataggio con dati mock
        if (isNewRegistry) {
          savedRegistry = {
            ...payload,
            id: mockRegistries.length + 1,
            entryCount: 0,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          };
          mockRegistries.push(savedRegistry);
        } else {
          const index = mockRegistries.findIndex(r => r.id === currentRegistry.id);
          if (index !== -1) {
            savedRegistry = {
              ...mockRegistries[index],
              name: currentRegistry.name,
              description: currentRegistry.description,
              updatedAt: new Date().toISOString()
            };
            mockRegistries[index] = savedRegistry;
          }
        }
        
        setSnackbar({
          open: true,
          message: isNewRegistry ? 'Anagrafica creata con successo (modalità demo)' : 'Anagrafica aggiornata con successo (modalità demo)',
          severity: 'success'
        });
      } else {
        // Salvataggio reale tramite API
        if (isNewRegistry) {
          savedRegistry = await registryService.create(payload);
          setSnackbar({
            open: true,
            message: 'Anagrafica creata con successo',
            severity: 'success'
          });
        } else {
          savedRegistry = await registryService.update(currentRegistry.id, payload);
          setSnackbar({
            open: true,
            message: 'Anagrafica aggiornata con successo',
            severity: 'success'
          });
        }
      }
      
      fetchRegistries(page, rowsPerPage, searchTerm); // Ricarica le anagrafiche per avere i dati aggiornati
      handleCloseRegistryDialog();
    } catch (err) {
      console.error('Error saving registry:', err);
      if (err?.response?.status === 400) setSourceErrors(fieldErrorsByPath(err.response.data?.fieldErrors));
      setSnackbar({
        open: true,
        message: registryErrorMessage(err, 'Errore durante il salvataggio dell\'anagrafica'),
        severity: 'error'
      });
    } finally {
      savingRegistryRef.current = false;
      setSavingRegistry(false);
    }
  };
  
  // Eliminazione di un'anagrafica (dopo conferma)
  const handleConfirmDeleteRegistry = async () => {
    const id = registryToDelete?.id;
    if (id == null) return; // click sul dialogo mentre si sta chiudendo
    setDeleting(true);
    try {
      if (usingMockData) {
        // Rimuovi l'anagrafica dai dati mock
        const index = mockRegistries.findIndex(r => r.id === id);
        if (index !== -1) {
          mockRegistries.splice(index, 1);
        }
        
        setSnackbar({
          open: true,
          message: 'Anagrafica eliminata con successo (modalità demo)',
          severity: 'success'
        });
      } else {
        await registryService.delete(id);
        setSnackbar({
          open: true,
          message: 'Anagrafica eliminata con successo',
          severity: 'success'
        });
      }
      
      setAnchorEl(null);
      // se era l'unica anagrafica della pagina, torna alla precedente (altrimenti resta una pagina vuota)
      const targetPage = registries.length === 1 && page > 0 ? page - 1 : page;
      setPage(targetPage);
      fetchRegistries(targetPage, rowsPerPage, searchTerm); // Ricarica le anagrafiche per avere i dati aggiornati
    } catch (err) {
      console.error('Error deleting registry:', err);
      setSnackbar({
        open: true,
        message: registryErrorMessage(err, 'Errore durante l\'eliminazione dell\'anagrafica'),
        severity: 'error'
      });
    } finally {
      setDeleting(false);
      setRegistryToDelete(null);
    }
  };
  
  // Apertura del dialogo delle voci
  const handleOpenEntriesDialog = (registry) => {
    setSelectedRegistry(registry);
    setOpenEntriesDialog(true);
  };

  // Chiusura del dialogo delle voci: l'anagrafica resta nel titolo durante la transizione
  // di chiusura e viene azzerata in onExited (il hook delle voci è già gestito da openEntriesDialog)
  const handleCloseEntriesDialog = () => {
    setOpenEntriesDialog(false);
  };

  // Import concluso (il dialogo si è chiuso dopo la scrittura): ricarica la tabella e avvisa
  const handleImported = (result) => {
    entriesState.reload();
    const n = (result?.inserted || 0) + (result?.updated || 0);
    setSnackbar({ open: true, message: n === 1 ? 'Importata 1 voce' : `Importate ${n} voci`, severity: 'success' });
  };

  // Apertura del dialogo per aggiungere una nuova voce
  const handleOpenNewEntryDialog = () => {
    setCurrentEntry(null);
    setEntryFormData({});
    setIsNewEntry(true);
    setOpenEntryDialog(true);
  };

  // Apertura del dialogo per modificare una voce esistente
  const handleOpenEditEntryDialog = (entry) => {
    setCurrentEntry(entry);
    setEntryFormData(entry.attributes || {});
    setIsNewEntry(false);
    setOpenEntryDialog(true);
  };

  // Chiusura del dialogo della voce
  const handleCloseEntryDialog = () => {
    setOpenEntryDialog(false);
    setCurrentEntry(null);
    setEntryFormData({});
  };

  // Salvataggio di una voce
  const handleSaveEntry = async (attributes) => {
    if (savingEntryRef.current) return; // click ravvicinati: una sola richiesta
    savingEntryRef.current = true;
    setSavingEntry(true);
    try {
      // Numeric fields come out of RegistryForm's <TextField type="number"> as strings;
      // coerce them to real JS numbers here (submit time only) so the backend validator
      // (which requires `instanceof Number`) and the JSONB `@>` filter accept them.
      const payload = coerceEntryAttributesToSchema(trimStringAttributes(attributes), selectedRegistry?.schema?.properties);
      // I campi bloccati non si trimmano: il backend confronta la chiave com'è memorizzata.
      lockedEntryFields.forEach((field) => { payload[field] = currentEntry.attributes[field]; });

      if (isNewEntry) {
        await registryService.createEntry(selectedRegistry.id, payload);
      } else {
        await registryService.updateEntry(selectedRegistry.id, currentEntry.id, payload);
      }

      entriesState.reload();

      setSnackbar({
        open: true,
        message: isNewEntry ? 'Voce aggiunta con successo' : 'Voce aggiornata con successo',
        severity: 'success'
      });

      handleCloseEntryDialog();
    } catch (err) {
      console.error('Error saving entry:', err);
      setSnackbar({ open: true, message: saveErrorMessage(err, selectedRegistry?.schema), severity: 'error' });
    } finally {
      savingEntryRef.current = false;
      setSavingEntry(false);
    }
  };

  // Eliminazione di una voce (dopo conferma); se era l'unica della pagina torna alla precedente
  const handleConfirmDeleteEntry = async () => {
    const entry = entryToDelete;
    if (!entry) return; // click sul dialogo mentre si sta chiudendo
    setDeleting(true);
    try {
      await registryService.deleteEntry(selectedRegistry.id, entry.id);
      if (entriesState.entries.length === 1 && entriesState.page > 0) {
        entriesState.setPage(entriesState.page - 1);
      } else {
        entriesState.reload();
      }
      setSnackbar({ open: true, message: 'Voce eliminata con successo', severity: 'success' });
    } catch (err) {
      console.error('Error deleting entry:', err);
      setSnackbar({
        open: true,
        message: registryErrorMessage(err, 'Errore nell\'eliminazione della voce'),
        severity: 'error'
      });
      entriesState.reload(); // es. 404 perché già eliminata altrove: la tabella riflette il server
    } finally {
      setDeleting(false);
      setEntryToDelete(null);
    }
  };

  // Gestione del menu contestuale
  const handleOpenMenu = (event, registry) => {
    setAnchorEl(event.currentTarget);
    setSelectedRegistryForMenu(registry);
  };
  
  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedRegistryForMenu(null);
  };
  
  // Chiusura dello snackbar
  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, gap: 2 }}>
              <Box>
                <Typography component="h1" variant="h5">
                  Anagrafiche
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Cataloghi di dati di riferimento usati per validare le voci estratte dai Codex.
                </Typography>
              </Box>
              {puoCreare && (
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={handleOpenNewRegistryDialog}
                  sx={{ flexShrink: 0 }}
                >
                  Nuova Anagrafica
                </Button>
              )}
            </Box>

            {usingMockData && error && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <RegistryTable
              schema={{
                properties: {
                  code: { name: 'Codice', type: 'string', visible_on_ui: true, searchable: true, mono: true },
                  name: { name: 'Nome', type: 'string', visible_on_ui: true, searchable: true },
                  description: { name: 'Descrizione', type: 'string', visible_on_ui: true, searchable: true },
                  sourceType: { name: 'Tipo Sorgente', type: 'string', visible_on_ui: true, searchable: true },
                  internal: { name: 'Interna', type: 'boolean', visible_on_ui: true, searchable: true }
                }
              }}
              data={registries}
              totalCount={totalCount}
              page={page}
              rowsPerPage={rowsPerPage}
              loading={loading}
              emptyTitle="Nessuna anagrafica"
              emptyHint="Crea la prima anagrafica con «Nuova Anagrafica» per iniziare a validare i dati."
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
              onEdit={handleOpenEditRegistryDialog}
              onDelete={puoEliminare ? (registry) => setRegistryToDelete(registry) : undefined}
              onSearch={handleSearch}
              onViewEntries={handleOpenEntriesDialog}
              onSync={puoModificare ? setSyncRegistry : undefined}
            />
          </Paper>
        </Grid>
      </Grid>

      {/* Registry Dialog */}
      <Dialog
        open={openRegistryDialog}
        onClose={savingRegistry ? undefined : handleCloseRegistryDialog}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          {isNewRegistry ? 'Nuova Anagrafica' : 'Modifica Anagrafica'}
        </DialogTitle>
        <DialogContent>
          <RegistryForm
            schema={{
              properties: {
                code: { name: 'Codice', type: 'string', required: true, visible_on_ui: true },
                name: { name: 'Nome', type: 'string', required: true, visible_on_ui: true },
                description: { name: 'Descrizione', type: 'text', visible_on_ui: true },
                internal: {
                  name: 'Anagrafica Interna',
                  type: 'boolean',
                  visible_on_ui: true
                }
              }
            }}
            data={currentRegistry}
            onChange={setCurrentRegistry}
          />
          {currentRegistry && currentRegistry.internal !== true && (
            <SourceConfigEditor
              key={currentRegistry.id || 'new'}
              sourceType={currentRegistry.sourceType ?? ''}
              sourceConfig={currentRegistry.sourceConfig}
              schemaProperties={Object.fromEntries(orderedPropertyEntries(currentRegistry.schema))}
              errors={sourceErrors}
              onChange={({ sourceType, sourceConfig }) =>
                setCurrentRegistry((prev) => ({ ...prev, sourceType, sourceConfig }))}
            />
          )}
          <SchemaEditor
            key={currentRegistry?.id || 'new'}
            value={currentRegistry?.schema}
            onChange={(schema) => setCurrentRegistry((prev) => ({
              ...prev,
              schema,
              // Un campo rimosso o rinominato porta via anche il suo mapping verso la sorgente.
              ...(prev?.sourceConfig?.mapping ? { sourceConfig: pruneMapping(prev.sourceConfig, schema) } : {}),
            }))}
            onValidityChange={setSchemaErrors}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRegistryDialog} disabled={savingRegistry}>Annulla</Button>
          {(isNewRegistry ? puoCreare : puoModificare) && (
            <Button onClick={handleSaveRegistry} variant="contained" color="primary" disabled={savingRegistry}>
              Salva
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Entries Dialog (con i dialoghi di import e storico) */}
      <RegistryEntriesDialog
        open={openEntriesDialog}
        registry={selectedRegistry}
        onClose={handleCloseEntriesDialog}
        onExited={() => setSelectedRegistry(null)}
        entriesState={entriesState}
        canEditEntries={puoModificareVoci}
        onNewEntry={handleOpenNewEntryDialog}
        onEditEntry={handleOpenEditEntryDialog}
        onDeleteEntry={setEntryToDelete}
        onImported={handleImported}
      />

      <RegistrySyncPanel
        open={!!syncRegistry}
        registry={syncRegistry}
        canSync={puoModificare}
        onClose={() => setSyncRegistry(null)}
      />

      {/* Entry Dialog */}
      <Dialog open={openEntryDialog} onClose={savingEntry ? undefined : handleCloseEntryDialog} maxWidth="md" fullWidth>
        <DialogTitle>{isNewEntry ? 'Nuova voce' : 'Modifica voce'}</DialogTitle>
        <DialogContent>
          <RegistryForm
            schema={selectedRegistry?.schema || { properties: {} }}
            data={entryFormData}
            onChange={setEntryFormData}
            lockedFields={lockedEntryFields}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEntryDialog} disabled={savingEntry}>Annulla</Button>
          <Button variant="contained" onClick={() => handleSaveEntry(entryFormData)} disabled={savingEntry}>Salva</Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={Boolean(entryToDelete)}
        title="Elimina voce"
        message={`Eliminare la voce «${lastEntryLabel.current}»?`}
        busy={deleting}
        onConfirm={handleConfirmDeleteEntry}
        onClose={() => setEntryToDelete(null)}
      />

      <ConfirmDialog
        open={Boolean(registryToDelete)}
        title="Elimina anagrafica"
        message={`Eliminare l'anagrafica «${lastRegistryLabel.current}» e tutte le sue voci?`}
        busy={deleting}
        onConfirm={handleConfirmDeleteRegistry}
        onClose={() => setRegistryToDelete(null)}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Registries; 